import React, { useEffect, useState, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { format, parseISO, differenceInMonths } from 'date-fns';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';

import {
  formatPrice,
  formataCNPJ,
  formataCep,
  formataCPF,
  formataTelefone,
} from '../../../utils/funcoes';
import numeroToExtenso from '../../../utils/numeroPorExtenso';

import Loading from '../../../components/Loading';

import { IContrato } from './Cadastro';

import { IUsuario } from '../index';
import getResponseErrors from '../../../utils/getResponseErrors';

interface IClinica {
  CliCodigo: number;
  CliNome: string;
  CliCNPJ: string;
  CliRepresentante: string;
  CliTelefone: string;
  CliTelefone2: string;
  CliEndereco: string;
  CliCidade: string;
  CliBairro: string;
  CliCEP: string;
  CliNumero: string;
  CliUF: string;
  CliComplemento: string;
  CliEnderecoCompleto: string;
  CliEmail: string;
}

interface IUsuarioContrato {
  clinica: IClinica;
  contrato: IContrato;
  contratoTexto: string;
  usuario: IUsuario;
}

interface IUrlParams {
  id: string;
}

const Contrato: React.FC = () => {
  const { addToast } = useToast();
  const { params } = useRouteMatch<IUrlParams>();
  const { id } = params;
  const [loading, setLoading] = useState(false);
  const [contrato, setContrato] = useState<string>('');

  const handleHTML = useCallback((texto: string) => {
    return texto
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }, []);

  const handleFormataUsuario = useCallback((usu: IUsuario) => {
    const UsuEnderecoCompleto = `${usu.UsuEndereco}, ${usu.UsuNumero} ${
      usu.UsuComplemento
    }, ${usu.UsuBairro}, ${usu.UsuCidade}/${usu.UsuUF} - CEP ${formataCep(
      usu.UsuCEP,
    )}`;

    const dependentesFormatado = {
      ...usu,
      UsuCPFFormatado: formataCPF(usu.UsuCPF),
      UsuDataNascFormatada: format(
        parseISO(usu.UsuDataNasc.toString()),
        'dd/MM/yyyy',
      ),
      UsuTelefoneFormatado: formataTelefone(usu.UsuTelefone),
      UsuEnderecoCompleto,
    };
    return dependentesFormatado;
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IUsuarioContrato>(
          `/medicos/contratocompleto/${id}`,
        );

        const clinica = {
          ...response.data.clinica,
          CliCNPJ: formataCNPJ(response.data.clinica.CliCNPJ),
          CliCEP: formataCep(response.data.clinica.CliCEP),
          CliTelefone: formataTelefone(response.data.clinica.CliTelefone),
          CliTelefone2: formataTelefone(response.data.clinica.CliTelefone2),
          CliEnderecoCompleto: `${response.data.clinica.CliEndereco}, ${
            response.data.clinica.CliNumero
          } ${response.data.clinica.CliComplemento}, ${
            response.data.clinica.CliBairro
          }, ${response.data.clinica.CliCidade}/${
            response.data.clinica.CliUF
          } - CEP ${formataCep(response.data.clinica.CliCEP)}`,
        };

        const usuario = handleFormataUsuario(response.data.usuario);

        const ConDataInicio = parseISO(
          response.data.contrato.ConDataInicio.toString(),
        );
        const ConDataFim = parseISO(
          response.data.contrato.ConDataFim.toString(),
        );
        const mesesContrato = `${differenceInMonths(
          ConDataFim,
          ConDataInicio,
        )} meses`;

        const contratoDados: IContrato = {
          ...response.data.contrato,
          ConDataInicioFormatado: format(ConDataInicio, 'dd/MM/yyyy'),
          ConDataFimFormatado: format(ConDataFim, 'dd/MM/yyyy'),
          ConValorFormatado:
            response.data.contrato.ConTipo === 'Por Percentual'
              ? response.data.contrato.ConValor.toFixed(2)
              : formatPrice(response.data.contrato.ConValor),
          ConValorExtraFormatado: formatPrice(
            response.data.contrato.ConValorExtra,
          ),
        };

        const remuneracao = contratoDados.ConRemuneracao.replace(
          /\[VALOR_CONTRATO]/g,
          contratoDados.ConValorFormatado,
        )
          .replace(/\[VALOR_EXTENSO]/g, numeroToExtenso(contratoDados.ConValor))
          .replace(
            /\[VALOR_EXTRA_CONTRATO]/g,
            contratoDados.ConValorExtraFormatado,
          )
          .replace(
            /\[VALOR_EXTRA_EXTENSO]/g,
            numeroToExtenso(contratoDados.ConValorExtra),
          );

        const contratoFormatado = response.data.contratoTexto
          .replace(/\[NOME_MEDICO]/g, usuario.UsuNome)
          .replace(/\[CPF_MEDICO]/g, usuario.UsuCPFFormatado)
          .replace(/\[RG_MEDICO]/g, usuario.UsuRG)
          .replace(/\[ESPECIALIDADE_MEDICO]/g, usuario.UsuEspecialidade)
          .replace(/\[CRM_MEDICO]/g, `${usuario.UsuConselho} ${usuario.UsuCRM}`)
          .replace(/\[ENDERECO_COMPLETO_MEDICO]/g, usuario.UsuEnderecoCompleto)
          .replace(/\[NOME_CLINICA]/g, clinica.CliNome)
          .replace(/\[CNPJ_CLINICA]/g, clinica.CliCNPJ)
          .replace(/\[REPRESENTANTE_CLINICA]/g, clinica.CliRepresentante)
          .replace(/\[TELEFONE_CLINICA]/g, clinica.CliTelefone)
          .replace(/\[TELEFONE2_CLINICA]/g, clinica.CliTelefone2)
          .replace(/\[CIDADE_CLINICA]/g, clinica.CliCidade)
          .replace(/\[ESTADO_CLINICA]/g, clinica.CliUF)
          .replace(/\[EMAIL_CLINICA]/g, clinica.CliEmail)
          .replace(/\[ENDERECO_COMPLETO_CLINICA]/g, clinica.CliEnderecoCompleto)
          .replace(/\[REMUNERACAO_MEDICO]/g, remuneracao)
          .replace(
            /\[DATA_CONTRATO_MEDICO]/g,
            contratoDados.ConDataInicioFormatado,
          )
          .replace(
            /\[QUANTIDADE_ATENDIMENTO]/g,
            contratoDados.ConQuantidade.toString(),
          )
          .replace(/\[VALIDADE_CONTRATO_MEDICO]/g, mesesContrato);
        // .replace(/\[VALOR_PARCELA_ESCRITO]/g, numeroToExtenso(PlaValor))
        // .replace(/\[VALOR_TOTAL_ESCRITO]/g, numeroToExtenso(PlaValor * 12))
        // .replace(/\[DATA_ADESAO]/g, PlaDataInicioFormatado);
        // .replace(/\table/g, "table class='tabela-contrato'");

        setContrato(handleHTML(contratoFormatado));
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [id, addToast, handleHTML, handleFormataUsuario]);

  return (
    <div className="p-0 m-0 bg-white">
      {loading && <Loading />}
      {contrato && <span dangerouslySetInnerHTML={{ __html: contrato }} />}
    </div>
  );
};

export default Contrato;
